/**
 * Countries are ISO 3166-1 alpha-2 codes — the same thing the backend stores and Odoo exports.
 *
 * Only the codes live here. Their names come from the browser through `Intl.DisplayNames`, in
 * whichever language the interface is showing, so nobody maintains a translated country list and
 * "Switzerland" and "Schweiz" cannot drift apart from each other.
 */

/** The ones a stand actually meets, offered first so the usual case is one scroll-free pick. */
export const FREQUENT_COUNTRIES = ['DE', 'AT', 'CH'] as const

/**
 * Every other country, alphabetical by code.
 *
 * The whole of ISO 3166-1 alpha-2, not a selection: a fair is where the one customer from Malta
 * turns up, and a picker that does not offer their country leaves the address wrong rather than
 * merely unset. This is generated from `java.util.Locale.getISOCountries()`, which is the list
 * `CountryCode` in the backend validates against — so nothing offered here comes back refused.
 * The three above are left out to keep them from appearing twice.
 *
 * A code already in the data that is not in this list still displays — see `countryName`.
 */
export const COUNTRIES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AU', 'AW', 'AX', 'AZ', 'BA',
  'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT',
  'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR',
  'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER',
  'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI',
  'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT',
  'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE',
  'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR',
  'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN',
  'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF',
  'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL',
  'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX',
  'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV',
  'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF',
  'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const

/**
 * One formatter per language, kept.
 *
 * Building an `Intl.DisplayNames` loads locale data, which is far from free — and the picker sorts
 * its list by the names, so a comparison-per-pair would build hundreds of them for a single render
 * of a form somebody is typing into. That is not a theoretical cost: it took a dialog's test file
 * from seconds to a minute and a half.
 */
const formatters = new Map<string, Intl.DisplayNames>()

function formatter(language: string): Intl.DisplayNames | null {
  const existing = formatters.get(language)
  if (existing) {
    return existing
  }
  try {
    const made = new Intl.DisplayNames([language], { type: 'region' })
    formatters.set(language, made)
    return made
  } catch {
    return null
  }
}

/**
 * The country's name in the given language, or the code itself when the browser has no name for
 * it — which is what keeps a record readable that carries something the picker does not offer.
 */
export function countryName(code: string, language: string): string {
  try {
    return formatter(language)?.of(code) ?? code
  } catch {
    // An invalid code makes `of` throw rather than return undefined.
    return code
  }
}
